import { ExternalLink } from "lucide-react";

import { Skeleton } from "@/components/ui/skeleton";

import { cn } from "@/lib/utils";
import type {
  OverviewV2ExternalSignal,
  OverviewV2ExternalSignalProvider,
} from "@/schema/brand-os/paid-media-overview-v2";

import { formatLeadTime } from "./format";

interface ExternalSignalsProps {
  provider: OverviewV2ExternalSignalProvider;
  signals: OverviewV2ExternalSignal[];
}

/**
 * Sentiment-coloured chip strip. The contract doesn't carry an explicit
 * sentiment, so we infer it from `category` + `lead_time_days`:
 *
 *   - regulatory / competitor / negative-leaning macro = red ("watch out")
 *   - active-now (lead = 0) and not negative = green ("happening now")
 *   - upcoming (lead > 0) = amber ("plan ahead")
 *
 * That's the same convention the mock uses (mixed red/amber/green dots).
 * If the backend ever ships a `sentiment` field we should swap this out.
 */
type Tone = "negative" | "positive" | "neutral";

const NEGATIVE_CATEGORIES = new Set(["regulatory", "competitor"]);
const POSITIVE_CATEGORIES = new Set(["seasonality", "platform_update"]);

function inferTone(signal: OverviewV2ExternalSignal): Tone {
  if (NEGATIVE_CATEGORIES.has(signal.category)) return "negative";
  if (signal.lead_time_days <= 0 && POSITIVE_CATEGORIES.has(signal.category)) return "positive";
  return "neutral";
}

export function ExternalSignals({ provider: _provider, signals }: ExternalSignalsProps) {
  if (!signals.length) {
    return (
      <p className="text-sm text-muted-foreground">
        No outside signals flagged for the current window.
      </p>
    );
  }
  return (
    <div className="flex flex-wrap gap-2">
      {signals.map((signal, i) => (
        <SignalChip key={`${signal.label}-${i}`} signal={signal} />
      ))}
    </div>
  );
}

function SignalChip({ signal }: { signal: OverviewV2ExternalSignal }) {
  const tone = inferTone(signal);

  const dotColour =
    tone === "negative" ? "bg-destructive" : tone === "positive" ? "bg-success" : "bg-amber-500";

  const content = (
    <span className="inline-flex items-center gap-2 rounded-full border bg-background px-3 py-1.5 text-xs text-foreground/90 hover:bg-muted transition-colors">
      <span className={cn("size-1.5 rounded-full shrink-0", dotColour)} />
      <span className="truncate max-w-[420px]">{signal.label}</span>
      <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
        · {formatLeadTime(signal.lead_time_days)}
      </span>
      {signal.evidence_url && <ExternalLink className="size-3 text-muted-foreground/70" />}
    </span>
  );

  // Backend either returns absolute http(s) URLs (web_search) or null (curated).
  // We guard so a stray relative URL never renders as a link.
  if (signal.evidence_url && signal.evidence_url.startsWith("http")) {
    return (
      <a href={signal.evidence_url} target="_blank" rel="noopener noreferrer" className="no-underline">
        {content}
      </a>
    );
  }
  return content;
}

export function ExternalSignalsSkeleton({ count = 5 }: { count?: number }) {
  return (
    <div className="flex flex-wrap gap-2">
      {Array.from({ length: count }, (_, i) => (
        <Skeleton key={i} className="h-7 w-40 rounded-full" />
      ))}
    </div>
  );
}
